import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";

// Constants
const RENDER_PIPELINE_ALPHA = 0.001; // Keep in pipeline without visible render
const VISIBLE_ALPHA = 1;

// Import positioning configs from progress bar
// Portrait progress bar configuration (horizontal bar on top)
const PORTRAIT_CONFIG = {
  scaleMultiplier: 1.0,
  xOffsetMultiplier: 0,
  yOffsetMultiplier: 0.6,
};

// Landscape progress bar configuration (vertical bar on side)
const LANDSCAPE_CONFIG = {
  scaleMultiplier: 1.0,
  xOffsetMultiplier: -0.45,
  yOffsetMultiplier: 0.5,
};

/**
 * HoldAndWinProgressBarTensionOverlayController
 * Manages ONLY tension animations for progress bar milestones (15 and 20)
 * Completely independent from progress bar animations
 */
class HoldAndWinProgressBarTensionOverlayController {
  constructor(
    container,
    dimensions,
    symbolWidth,
    symbolHeight,
    device,
    orientation
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Spine asset data
    this.tensionSpineData = {
      skeleton: "hns_progress_tension-skeleton",
      atlas: "hns_progress_tension-atlas",
    };

    // Two spine instances - one for each orientation
    this.portraitSpine = null;
    this.landscapeSpine = null;

    // Separate containers for each spine
    this.portraitContainer = null;
    this.landscapeContainer = null;

    // Tension state tracking
    this.isVisible = false;
    this.initialized = false;
    this.isCleanedUp = false;
    this.currentMilestone = null; // 15 or 20
    this.currentTimeouts = [];
    this.isTensionActive = false; // Track if tension animation is currently active
    this.tensionSoundPlaying = false; // Track if tension sound is currently playing

    this.initializeTensionSpines();
  }

  /**
   * Check if controller is ready for operations
   */
  isReady() {
    return (
      this.portraitSpine?.state &&
      this.landscapeSpine?.state &&
      this.initialized &&
      !this.isCleanedUp
    );
  }

  /**
   * Clear all active timeouts
   */
  clearAllTimeouts() {
    this.currentTimeouts.forEach(clearTimeout);
    this.currentTimeouts = [];
  }

  /**
   * Get device-specific scale multiplier
   */
  getDeviceScaleMultiplier() {
    return 1.0;
  }

  /**
   * Apply proportional scaling and centering to both spine animations
   * Matches progress bar positioning exactly
   */
  applyScaleAndCenter() {
    if (!this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let baseScale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const deviceMultiplier = this.getDeviceScaleMultiplier();
    baseScale *= deviceMultiplier;

    // Apply to portrait spine with config
    if (this.portraitSpine && this.portraitContainer) {
      const portraitScale = baseScale * PORTRAIT_CONFIG.scaleMultiplier;
      this.portraitSpine.scale.set(portraitScale);
      this.portraitSpine.x = 0;
      this.portraitSpine.y = 0;

      const xOffset = this.symbolWidth * PORTRAIT_CONFIG.xOffsetMultiplier;
      const yOffset = this.symbolHeight * PORTRAIT_CONFIG.yOffsetMultiplier;

      this.portraitContainer.x = xOffset;
      this.portraitContainer.y = yOffset;
    }

    // Apply to landscape spine with config
    if (this.landscapeSpine && this.landscapeContainer) {
      const landscapeScale = baseScale * LANDSCAPE_CONFIG.scaleMultiplier;
      this.landscapeSpine.scale.set(landscapeScale);
      this.landscapeSpine.x = 0;
      this.landscapeSpine.y = 0;

      const xOffset = this.symbolWidth * LANDSCAPE_CONFIG.xOffsetMultiplier;
      const yOffset = this.symbolHeight * LANDSCAPE_CONFIG.yOffsetMultiplier;

      this.landscapeContainer.x = xOffset;
      this.landscapeContainer.y = yOffset;
    }
  }

  /**
   * Create a tension spine instance
   */
  createTensionSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.tensionSpineData.skeleton) ||
        !assets.has(this.tensionSpineData.atlas)
      ) {
        return null;
      }

      const skeletonData = PIXI.Assets.get(this.tensionSpineData.skeleton);
      const atlasData = PIXI.Assets.get(this.tensionSpineData.atlas);

      if (!skeletonData || !atlasData) {
        return null;
      }

      const tensionSpine = Spine.from({
        skeleton: this.tensionSpineData.skeleton,
        atlas: this.tensionSpineData.atlas,
      });

      if (!tensionSpine || !tensionSpine.skeleton || !tensionSpine.state) {
        return null;
      }

      return tensionSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animation names from a spine instance
   */
  getAvailableAnimations(spine) {
    if (!spine?.skeleton?.data?.animations) {
      return [];
    }
    return spine.skeleton.data.animations.map((anim) => anim.name);
  }

  /**
   * Initialize both tension spine instances
   */
  initializeTensionSpines() {
    // Create portrait container and spine
    this.portraitContainer = new PIXI.Container();
    this.portraitContainer.name = "tension_portrait_container";
    this.portraitContainer.sortableChildren = true;
    this.container.addChild(this.portraitContainer);

    this.portraitSpine = this.createTensionSpine();
    if (this.portraitSpine) {
      this.portraitSpine.alpha = RENDER_PIPELINE_ALPHA;
      this.portraitSpine.zIndex = 10;
      this.portraitContainer.addChild(this.portraitSpine);
    }

    // Create landscape container and spine
    this.landscapeContainer = new PIXI.Container();
    this.landscapeContainer.name = "tension_landscape_container";
    this.landscapeContainer.sortableChildren = true;
    this.container.addChild(this.landscapeContainer);

    this.landscapeSpine = this.createTensionSpine();
    if (this.landscapeSpine) {
      this.landscapeSpine.alpha = RENDER_PIPELINE_ALPHA;
      this.landscapeSpine.zIndex = 10;
      this.landscapeContainer.addChild(this.landscapeSpine);
    }

    // Apply initial scaling and positioning
    this.applyScaleAndCenter();

    // Set initial visibility based on orientation
    this.updateOrientation(this.orientation);

    this.initialized = true;
  }

  /**
   * Update orientation visibility
   * Matches progress bar logic: show landscape if orientation is LANDSCAPE OR device is DESKTOP
   */
  updateOrientation(newOrientation) {
    this.orientation = newOrientation;

    if (!this.isReady()) return;

    const isLandscape = this.orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";

    // Show landscape if orientation is landscape OR device is desktop
    // This matches the progress bar logic exactly
    const shouldShowLandscape = isLandscape || isDesktop;

    // Show/hide containers based on orientation and device
    if (this.portraitContainer) {
      this.portraitContainer.visible = !shouldShowLandscape;
    }
    if (this.landscapeContainer) {
      this.landscapeContainer.visible = shouldShowLandscape;
    }
  }

  /**
   * Update dimensions for responsive scaling
   */
  updateDimensions(dimensions, symbolWidth, symbolHeight, device, orientation) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    this.applyScaleAndCenter();
    this.updateOrientation(orientation);
  }

  /**
   * Hide tension animation immediately
   */
  hideTension() {
    if (!this.isReady()) {
      this.isVisible = false;
      return;
    }

    this.clearAllTimeouts();

    // Mark tension as inactive
    this.isTensionActive = false;
    this.tensionSoundPlaying = false;

    // Stop tension sound
    soundManager.stop(SOUND_NAME.progress_bar_tension);

    requestAnimationFrame(() => {
      if (!this.isReady()) {
        this.isVisible = false;
        return;
      }

      try {
        if (this.portraitSpine) {
          this.portraitSpine.alpha = RENDER_PIPELINE_ALPHA;
          this.portraitSpine.state.clearTracks();
        }
        if (this.landscapeSpine) {
          this.landscapeSpine.alpha = RENDER_PIPELINE_ALPHA;
          this.landscapeSpine.state.clearTracks();
        }

        this.isVisible = false;
        this.currentMilestone = null;
      } catch (error) {
        // Silent fail
      }
    });
  }

  /**
   * Force stop tension animations with smooth exit transition
   */
  forceStopTension() {
    if (!this.isReady()) {
      this.hideTension();
      return;
    }

    this.clearAllTimeouts();

    // Mark tension as inactive
    this.isTensionActive = false;
    this.tensionSoundPlaying = false;

    // Stop tension sound
    soundManager.stop(SOUND_NAME.progress_bar_tension);

    // Determine active spine using same logic as progress bar
    const isLandscape = this.orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";
    const shouldShowLandscape = isLandscape || isDesktop;

    const activeSpine = shouldShowLandscape
      ? this.landscapeSpine
      : this.portraitSpine;

    if (!activeSpine || !this.currentMilestone) {
      this.hideTension();
      return;
    }

    try {
      // Use correct animation prefix based on active spine
      const animationPrefix = shouldShowLandscape ? "landscape_progress_tension" : "progress_tension";
      const outAnimation = `${animationPrefix}_${this.currentMilestone}_out`;
      const animations = this.getAvailableAnimations(activeSpine);

      if (animations.includes(outAnimation)) {
        activeSpine.state.setAnimation(0, outAnimation, false);

        const animationDuration =
          activeSpine.skeleton.data.animations.find(
            (anim) => anim.name === outAnimation
          )?.duration || 1.0;

        const timeoutId = setTimeout(() => {
          this.hideTension();
        }, animationDuration * 1000);

        this.currentTimeouts.push(timeoutId);
      } else {
        this.hideTension();
      }
    } catch (error) {
      this.hideTension();
    }
  }

  /**
   * Play tension intro animation and transition to idle loop
   * @param {number} milestone - 15 or 20
   * @param {string} orientation - PORTRAIT or LANDSCAPE
   * @param {boolean} playSound - Whether to play tension sound (default: true)
   */
  playTensionIn(milestone, orientation, playSound = true) {
    if (!this.isReady()) return;

    this.clearAllTimeouts();
    this.currentMilestone = milestone;
    this.updateOrientation(orientation);

    // Mark tension as active
    this.isTensionActive = true;

    // Start looping tension sound
    // Only play if sound is enabled (skip during intro popup or reload dialog)
    if (playSound) {
      soundManager.play(SOUND_NAME.progress_bar_tension);
      this.tensionSoundPlaying = true;
    } else {
      this.tensionSoundPlaying = false;
    }

    // Determine active spine using same logic as progress bar
    const isLandscape = orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";
    const shouldShowLandscape = isLandscape || isDesktop;

    const activeSpine = shouldShowLandscape
      ? this.landscapeSpine
      : this.portraitSpine;

    if (!activeSpine) return;

    requestAnimationFrame(() => {
      if (!activeSpine || this.isCleanedUp) return;

      try {
        activeSpine.state.clearTracks();
        activeSpine.state.clearListeners();
        activeSpine.state.apply(activeSpine.skeleton);
        activeSpine.skeleton.setToSetupPose();

        activeSpine.alpha = VISIBLE_ALPHA;
        this.isVisible = true;

        // Use correct animation prefix based on active spine
        const animationPrefix = shouldShowLandscape ? "landscape_progress_tension" : "progress_tension";
        const inAnimation = `${animationPrefix}_${milestone}_in`;
        const idleAnimation = `${animationPrefix}_${milestone}_idle`;
        const animations = this.getAvailableAnimations(activeSpine);

        if (
          animations.includes(inAnimation) &&
          animations.includes(idleAnimation)
        ) {
          activeSpine.state.setAnimation(0, inAnimation, false);
          activeSpine.state.addAnimation(0, idleAnimation, true, 0);
        } else if (animations.includes(idleAnimation)) {
          activeSpine.state.setAnimation(0, idleAnimation, true);
        }
      } catch (error) {
        this.hideTension();
      }
    });
  }

  /**
   * Play tension exit animation
   * Matches symbol tension pattern exactly
   */
  playTensionOut() {
    if (!this.isReady()) {
      return;
    }

    // If no milestone is set, tension never started - do nothing
    if (!this.currentMilestone) {
      return;
    }

    this.clearAllTimeouts();

    // Mark tension as inactive
    this.isTensionActive = false;
    this.tensionSoundPlaying = false;

    // Stop tension sound when exiting
    soundManager.stop(SOUND_NAME.progress_bar_tension);

    // Determine active spine using same logic as progress bar
    const isLandscape = this.orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";
    const shouldShowLandscape = isLandscape || isDesktop;

    const activeSpine = shouldShowLandscape
      ? this.landscapeSpine
      : this.portraitSpine;

    if (!activeSpine || !activeSpine.state) {
      return;
    }

    requestAnimationFrame(() => {
      if (!activeSpine || this.isCleanedUp || !activeSpine.state) {
        return;
      }

      try {
        // Clear and reset - same as symbol tension
        activeSpine.state.clearTracks();
        activeSpine.state.clearListeners();
        activeSpine.state.apply(activeSpine.skeleton);
        activeSpine.skeleton.setToSetupPose();

        // Use correct animation prefix based on active spine
        const animationPrefix = shouldShowLandscape ? "landscape_progress_tension" : "progress_tension";
        const outAnimation = `${animationPrefix}_${this.currentMilestone}_out`;
        const animations = this.getAvailableAnimations(activeSpine);

        let animationToPlay = null;

        if (animations.includes(outAnimation)) {
          animationToPlay = outAnimation;
        } else if (animations.length > 0) {
          animationToPlay = animations[0];
        }

        if (animationToPlay) {
          activeSpine.state.setAnimation(0, animationToPlay, false);

          const timeoutId = setTimeout(() => {
            this.hideTension();
          }, 1000);
          this.currentTimeouts.push(timeoutId);
        } else {
          this.hideTension();
        }
      } catch (error) {
        this.hideTension();
      }
    });
  }

  /**
   * Start tension sound if tension is active but sound isn't playing
   * Called when popups close to resume sound for active tension
   */
  resumeTensionSoundIfNeeded() {
    if (this.isTensionActive && !this.tensionSoundPlaying) {
      soundManager.play(SOUND_NAME.progress_bar_tension);
      this.tensionSoundPlaying = true;
    }
  }

  /**
   * Clean up spine resources and references
   */
  cleanup() {
    this.clearAllTimeouts();
    this.isCleanedUp = true;

    // Stop tension sound
    soundManager.stop(SOUND_NAME.progress_bar_tension);

    // Cleanup portrait spine
    if (this.portraitSpine && !this.portraitSpine.destroyed) {
      try {
        if (this.portraitSpine.state) {
          this.portraitSpine.state.clearTracks();
          this.portraitSpine.state.clearListeners();
        }
        if (this.portraitSpine.parent) {
          this.portraitSpine.parent.removeChild(this.portraitSpine);
        }
        this.portraitSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    // Cleanup landscape spine
    if (this.landscapeSpine && !this.landscapeSpine.destroyed) {
      try {
        if (this.landscapeSpine.state) {
          this.landscapeSpine.state.clearTracks();
          this.landscapeSpine.state.clearListeners();
        }
        if (this.landscapeSpine.parent) {
          this.landscapeSpine.parent.removeChild(this.landscapeSpine);
        }
        this.landscapeSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    // Cleanup containers
    if (this.portraitContainer && !this.portraitContainer.destroyed) {
      try {
        if (this.portraitContainer.parent) {
          this.portraitContainer.parent.removeChild(this.portraitContainer);
        }
        this.portraitContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }
    }

    if (this.landscapeContainer && !this.landscapeContainer.destroyed) {
      try {
        if (this.landscapeContainer.parent) {
          this.landscapeContainer.parent.removeChild(this.landscapeContainer);
        }
        this.landscapeContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }
    }

    // Clear references
    this.portraitSpine = null;
    this.landscapeSpine = null;
    this.portraitContainer = null;
    this.landscapeContainer = null;
    this.container = null;
    this.tensionSpineData = null;
    this.isVisible = false;
    this.initialized = false;
    this.currentMilestone = null;
  }
}

export default HoldAndWinProgressBarTensionOverlayController;
